import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {switchMap} from 'rxjs/internal/operators/switchMap';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    RiskActionsEnum,
    IndividualsService,
    IndividualInterface,
    RiskClassModel,
    IndividualModel,
    IndividualExtendedModel,
} from '@spa/api';

import {InArrayValidatorKey, RequiredValidatorKey} from '@spa/validators';

import {RadioGroupControl, Helper} from '../../../../shared/classes';

import {FinancialSanctionsFormErrorStatesEnum} from '../../enums';

import {FinancialSanctionsControl} from '../../../../../assets/descriptors/panel-clients';

export class FinancialSanctionsForm {
    private individual: IndividualExtendedModel;
    private individualsService: IndividualsService;
    private riskClassList: RiskClassModel[];

    public control: RadioGroupControl;

    constructor(
        individual: IndividualExtendedModel,
        individualsService: IndividualsService,
        riskClassList: RiskClassModel[]
    ) {
        this.individual = individual;
        this.individualsService = individualsService;
        this.riskClassList = riskClassList;
    }

    initControl(): void {
        this.control = FinancialSanctionsControl();

        this.riskClassList.map((classification: RiskClassModel) => {
            if (classification.riskAction !== RiskActionsEnum.Refuse) {
                this.control.options[0].value = classification.upperBound;
            } else {
                this.control.options[1].value = classification.upperBound;
            }
        });

        if (!this.individual.hasFinancialSanctions) {
            this.control.value = this.control.options[0].value;
        } else {
            this.control.value = this.control.options[1].value;
        }
    }

    validateControl(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if ((this.control.value === null) || (typeof this.control.value === 'undefined')) {
            this.control.setError(messageFunction(this.control.placeholder)[RequiredValidatorKey]);

            return false;
        }

        const riskScores = this.riskClassList.map((classification: RiskClassModel) => classification.upperBound);

        if (riskScores.indexOf(this.control.value as number) < 0) {
            this.control.setError(messageFunction(this.control.placeholder)[InArrayValidatorKey]);

            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        let riskActionCheck = false;

        const filteredRiskScores = this.riskClassList.filter((classification: RiskClassModel) => {
            return classification.upperBound === this.control.value;
        });

        if (filteredRiskScores.length !== 0) {
            riskActionCheck = filteredRiskScores[0].riskAction === RiskActionsEnum.Refuse;
        }

        if (this.individual.hasFinancialSanctions !== riskActionCheck) {
            return this.individualsService.getById(this.individual.id)
                .pipe(
                    switchMap((getResponse: ApiResponse<ApiError[] | IndividualInterface>) => {
                        if (getResponse.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponseObservable<IndividualInterface>(
                                getResponse,
                                FinancialSanctionsFormErrorStatesEnum.GetIndividualFailed
                            );
                        }

                        const data = getResponse.data as IndividualInterface;
                        const model = new IndividualModel(data);
                        model.forceAlias = false;

                        if (filteredRiskScores.length !== 0) {
                            model.hasFinancialSanctions = riskActionCheck;
                        }

                        return this.individualsService.update(model)
                            .pipe(
                                map((updateResponse: ApiResponse<ApiError[] | null>) => {
                                    if (updateResponse.status !== HttpStatusCodesEnum.Ok) {
                                        return Helper.errorsHelper().createErrorResponse<null>(
                                            updateResponse,
                                            FinancialSanctionsFormErrorStatesEnum.UpdateIndividualFailed
                                        );
                                    }

                                    this.individual.hasFinancialSanctions = riskActionCheck;

                                    return Helper.createSuccessResponse<boolean>(true);
                                })
                            );
                    })
                );
        }

        return null;
    }
}
